// Export history page.
//
// Replaces the old "Saved reports" placeholder with a log of everything the
// customer has actually pulled or sent: composed documents (download / email)
// and standalone table downloads. Each row keeps enough recipe to re-download
// on demand, so the history doubles as a quick re-run surface.
//
// Postgres mapping: reads `user_exports` (see exportData.js logExport) ordered
// by created_at desc; "Download again" re-resolves current data rather than
// storing the rendered blob.
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Button, Tooltip, Snackbar } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MainCard from '../../../components/cards/MainCard';
import useFeature from '../useFeature';
import { assembleSections, LARGE_TABLES, downloadBlob, periodLabel, largeTableRequest, largeTableLiveRows } from '../exportData';
import { resolveDateRange } from '../hooks/useDashboardData';
import { getExcludeDisabled } from '../disabledFilter';
import { dashboardApi } from '../../../store/services/dashboard.service';
import { generateDocument, generateTable } from '../exportGenerators';
import { useGetExportHistoryQuery, useClearExportHistoryMutation } from '../../../store/services/reportLayout.service';

function fmtAt(iso) {
    try {
        return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
}

const VIA_LABEL = { download: 'Downloaded', email: 'Emailed', scheduled: 'Scheduled' };

export default function ExportHistoryPage() {
    const benchmarkOn = useFeature('industryBenchmarking');
    const { tenancyId, tenancyName } = useSelector((state) => state.tenancy);
    const brand = (tenancyName || '').trim() || 'Boxphish';
    // Server-side per-tenancy history - shared across admins and devices.
    const historyQ = useGetExportHistoryQuery(tenancyId, { skip: tenancyId == null });
    const [clearHistory] = useClearExportHistoryMutation();
    const history = historyQ.data?.entries ?? [];
    const [toast, setToast] = useState('');
    const [fetchDashboard] = dashboardApi.endpoints.getDashboardData.useLazyQuery();

    const liveTableRows = async (e) => {
        const tenancy = e.scopeQuery?.tenancy;
        if (!Array.isArray(tenancy) || !tenancy.length) return undefined;
        const request = largeTableRequest(e.tableId, { tenancy });
        if (!request) return undefined;
        const { period, startDate, endDate, ...rest } = request;
        const data = await fetchDashboard({
            tenancyId: Number(tenancyId),
            ...rest,
            ...(getExcludeDisabled() ? { scope: { ...rest.scope, excludeDisabled: true } } : {}),
            filters: { dateRange: resolveDateRange({ startDate, endDate, period }) },
        }, true).unwrap();
        return largeTableLiveRows(e.tableId, data) ?? undefined;
    };

    const redownload = async (e) => {
        try {
            let file;
            if (e.kind === 'table') {
                const t = LARGE_TABLES.find((x) => x.id === e.tableId);
                if (!t) { setToast('That table is no longer available'); return; }
                file = generateTable(e.format, t.label, t.columns, t.getRows(await liveTableRows(e)), { brand });
            } else {
                const sections = assembleSections(e.sectionIds || [], e.drillIds || [], { benchmarkOn, period: e.period });
                if (sections.length === 0) { setToast('Those sections are no longer available'); return; }
                file = await generateDocument(e.format, e.title, sections, { brand, periodLabel: e.period ? periodLabel(e.period) : null });
            }
            downloadBlob(file.content, file.mime, file.filename);
            setToast(`Re-downloaded ${file.filename}`);
        } catch {
            setToast('Could not re-download this export');
        }
    };

    const clearAll = async () => {
        try {
            await clearHistory(tenancyId).unwrap();
            setToast('History cleared');
        } catch {
            setToast('Could not clear the history - please try again');
        }
    };

    return (
        <Box sx={{ p: '6px 24px 48px', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>Export history</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
                    Every export you have pulled or sent. Re-download any of them with the latest data.
                </Typography>
            </Box>

            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>Pulled exports</Typography>
                        <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{history.length} record{history.length === 1 ? '' : 's'}</Typography>
                    </Box>
                    {history.length > 0 && (
                        <Button
                            size="small" variant="outlined"
                            startIcon={<DeleteSweepOutlinedIcon sx={{ fontSize: 15 }} />}
                            onClick={clearAll}
                            sx={{ textTransform: 'none', fontSize: 11, borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'error.main', color: 'error.main' } }}
                        >
                            Clear history
                        </Button>
                    )}
                </Box>

                {history.length === 0 ? (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{historyQ.isLoading ? 'Loading history\u2026' : 'No exports yet.'}</Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.5 }}>Anything you download or email from the export builder or data tables shows up here.</Typography>
                    </Box>
                ) : (
                    <Box sx={{ p: 1.5 }}>
                        {history.map((e) => {
                            const Icon = e.kind === 'table' ? TableChartOutlinedIcon : DescriptionOutlinedIcon;
                            const detail = e.kind === 'table'
                                ? `${(e.rows ?? 0).toLocaleString()} rows`
                                : `${e.sectionCount ?? (e.sectionIds ? e.sectionIds.length : 0)} sections`;
                            return (
                                <Box key={e.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 1.5, py: 1.25, borderRadius: 1.5, '&:hover': { bgcolor: 'action.hover' } }}>
                                    <Icon sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />
                                    <Box sx={{ flex: 1, minWidth: 180 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.title}</Typography>
                                            <Typography sx={{ fontSize: 9.5, fontWeight: 600, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 0.75 }}>{e.format}</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>
                                            {fmtAt(e.at)} · {detail}
                                            {/* Who pulled it. Stamped server-side from the JWT, so it's an
                                                audit trail rather than something the client asserted.
                                                Older entries predate the field and simply omit it. */}
                                            {e.byEmail ? ` · by ${e.byEmail}` : ''}
                                            {e.via === 'email' && e.recipients?.length ? ` · to ${e.recipients.join(', ')}` : ''}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 10.5, color: 'text.disabled', flexShrink: 0 }}>
                                        {e.via === 'email' && <MailOutlineIcon sx={{ fontSize: 13 }} />}
                                        {VIA_LABEL[e.via] || e.via}
                                    </Box>
                                    <Tooltip title="Download again with the latest data">
                                        <Button
                                            size="small" variant="outlined"
                                            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 13 }} />}
                                            onClick={() => redownload(e)}
                                            sx={{ minWidth: 0, fontSize: 10.5, fontWeight: 600, px: 1, py: '2px', borderRadius: '4px', textTransform: 'none', borderColor: 'divider', color: 'text.secondary', flexShrink: 0, '& .MuiButton-startIcon': { mr: 0.5 }, '&:hover': { borderColor: 'secondary.main', color: 'secondary.main' } }}
                                        >
                                            Again
                                        </Button>
                                    </Tooltip>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </MainCard>

            <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
        </Box>
    );
}
